import React, { useState, useRef, useContext, useEffect } from 'react';
import { SecondaryButton } from 'jl-ui-kit/elements/button/Button';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import overrideCategory from 'lib/overrides/overrideCategory';
import styles from './MobileMenu.scss';
import MobileMenuItem from '../mobile-menu-item/MobileMenuItem';
import SubMenu from './SubMenu';
import formatTestId from '../../../utils/test-id/format-testid';

import EnvVarsFeaturesContext from '../../../scaffold/EnvVarsFeaturesContext';
import { getStaticMobileMenus } from '../../links';
import { UserContext } from '../../../scaffold/UserContext';

// Currently ATG will not return top level categories that have isDisplayedInGlobalNav=false
// Inspiration is a category that we want to display on Mobile and not Desktop so we manually add it to the end here
// Once ATG goes away, hopefully so can this.
const InspirationLink = {
  name: 'Inspiration',
  url: '/content',
  id: '10139580012',
};

const MobileMenu = ({
  menus: initialMenus = [],
  fetchChildCategories,
  hasContentBlocks,
}) => {
  const rootMenuName = initialMenus[0]?.name || '';
  const [rootMenuItems, setRootMenuItems] = useState([
    ...(initialMenus[0]?.child || []),
    InspirationLink,
  ]);
  const top = useRef();
  const [activeLevel, setActiveLevel] = useState(0);
  const {
    userState: {
      user: { signedIn },
    },
  } = useContext(UserContext);

  const {
    envVars: { accountBaseUrl },
    categoryOverrides,
  } = useContext(EnvVarsFeaturesContext);

  const staticMenus = getStaticMobileMenus(accountBaseUrl, signedIn);

  const scrollToTop = () => {
    if (top.current && top.current.scrollBy) {
      top.current.scrollBy(0, -1000);
    }
  };

  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const withContentBlocks = (existing, fetched) => {
    const contentBlock = existing?.child?.find(c => c.blocks);
    return contentBlock && fetched?.child?.length
      ? {
          ...fetched,
          child: [...fetched.child, contentBlock],
        }
      : fetched;
  };

  const fetchWithOverrides = async ({ id }) => {
    const fetchedCategories = await fetchChildCategories({ id });
    return categoryOverrides
      ? overrideCategory(fetchedCategories, categoryOverrides)
      : fetchedCategories;
  };

  const fetchSubMenuData = async (menuItemId, menuIndex) => {
    if (rootMenuItems[menuIndex].isCached) {
      return;
    }

    try {
      const fetchedData = await fetchWithOverrides({
        id: menuItemId,
      });

      if (mounted.current === false) {
        return;
      }

      if (!fetchedData || fetchedData.error) {
        throw new Error('Fetch failed');
      }

      setRootMenuItems(existingRootMenuItems =>
        existingRootMenuItems.map((existing, i) => {
          const fetched = hasContentBlocks
            ? withContentBlocks(existing, fetchedData)
            : fetchedData;
          return i === menuIndex
            ? {
                ...existing,
                child: fetched.child || [],
                isCached: true,
              }
            : existing;
        })
      );
    } catch (e) {
      console.error(`fetch failed for category id: ${menuItemId}`);
    }
  };

  const activeMenuStyles = [
    styles.activeLevelRoot,
    styles.activeLevelFirst,
    styles.activeLevelSecond,
    styles.activeLevelThird,
  ];
  const subMenuClassName = classNames([
    styles.subMenu,
    activeMenuStyles[activeLevel],
  ]);

  return (
    <>
      <nav
        className={styles.mobileMenu}
        ref={top}
        data-testid="mobile-navigation-menu"
      >
        <div
          className={subMenuClassName}
          data-testid="mobile-navigation-container"
        >
          <SubMenu
            level={0}
            setActiveLevel={setActiveLevel}
            subMenuData={{
              name: rootMenuName,
              child: rootMenuItems,
              backLabel: undefined,
            }}
            onItemSelected={(menuItemId, menuIndex) => {
              fetchSubMenuData(menuItemId, menuIndex);
            }}
            scrollToTop={scrollToTop}
          >
            {staticMenus.map(menu => (
              <React.Fragment key={menu.name}>
                <h3 className={styles.mobileMenu__name}>
                  {menu.url ? (
                    <a
                      href={menu.url}
                      data-testid={`${formatTestId(
                        menu.name,
                        'mobile-navigation-links-'
                      )}`}
                    >
                      {menu.name}
                    </a>
                  ) : (
                    menu.name
                  )}
                </h3>
                {menu.text && (
                  <p className={styles.mobileMenu__text}>{menu.text}</p>
                )}
                {menu.child && (
                  <ul className={styles.mobileMenuList}>
                    {menu.child.map(menuItem => {
                      const { name, url } = menuItem;
                      return (
                        <MobileMenuItem key={name} label={name} url={url} />
                      );
                    })}
                  </ul>
                )}
                {menu.button && (
                  <div className={styles.mobileMenu__button}>
                    <SecondaryButton
                      tag="a"
                      href={menu.button.url}
                      {...{ target: '_blank', rel: 'noopener' }}
                    >
                      {menu.button.label}
                    </SecondaryButton>
                  </div>
                )}
              </React.Fragment>
            ))}
          </SubMenu>
        </div>
      </nav>
    </>
  );
};

const itemShape = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
};
itemShape.child = PropTypes.arrayOf(PropTypes.shape(itemShape));

MobileMenu.defaultProps = {
  hasContentBlocks: false,
};

MobileMenu.propTypes = {
  menus: PropTypes.arrayOf(PropTypes.shape(itemShape)).isRequired,
  fetchChildCategories: PropTypes.func.isRequired,
  hasContentBlocks: PropTypes.bool,
};

export default MobileMenu;
